import { useRef } from 'react'
import { AnalyticsHeader } from '../features/analytics/components/AnalyticsHeader'
import { KpiCards } from '../features/analytics/components/KpiCards'
import { TasksByTeamChart, TaskCompletedChart } from '../features/analytics/components/BarCharts'
import { IncidentTypesChart } from '../features/analytics/components/DonutCharts'
import { HeatmapGrid } from '../features/analytics/components/HeatmapGrid'
import { SmartRecommendation } from '../features/analytics/components/SmartRecommendation'
import { LoadErrorState } from '../components/LoadErrorState'
import { useAnalytics } from '../features/analytics/hooks/useAnalytics'

const SMART_RECOMMENDATION_PLACEHOLDER =
  'The system will provide smart recommendations after gathering enough data.'

function AnalyticsSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6" data-testid="analytics-skeleton">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {['a', 'b', 'c'].map((key) => (
          <div key={key} className="h-20 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
        <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
      </div>
      <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
    </div>
  )
}

export function AnalyticsPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useAnalytics()
  const exportRef = useRef<HTMLDivElement>(null)

  async function handleExport(): Promise<void> {
    const container = exportRef.current
    if (!container) throw new Error('Report container is not ready')

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ])

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 8
    const contentWidth = pageWidth - margin * 2
    let cursorY = margin

    // Render each top-level section on its own so a section is never split across pages.
    const sections = Array.from(container.children) as HTMLElement[]
    for (const section of sections) {
      const canvas = await html2canvas(section, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
      const imgHeight = (canvas.height * contentWidth) / canvas.width
      if (cursorY > margin && cursorY + imgHeight > pageHeight - margin) {
        pdf.addPage()
        cursorY = margin
      }
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, cursorY, contentWidth, imgHeight)
      cursorY += imgHeight + 4
    }

    const now = new Date()
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    pdf.save(`analytics-report-${dateStr}.pdf`)
  }

  return (
    <div ref={exportRef} className="flex h-full flex-col gap-6 overflow-y-auto">
      <AnalyticsHeader onExport={handleExport} exportDisabled={isLoading || isError || !data} />
      {isError ? (
        <LoadErrorState message="Couldn't load analytics" onRetry={refetch} />
      ) : isLoading || !data ? (
        <AnalyticsSkeleton />
      ) : (
        <>
          <KpiCards stats={data.kpis} columns={3} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TasksByTeamChart data={data.tasksByTeam} />
            <IncidentTypesChart data={data.tasksByIntent} />
          </div>
          <TaskCompletedChart data={data.completedByMonth} />
          <HeatmapGrid data={data.heatmap} />
          <SmartRecommendation text={SMART_RECOMMENDATION_PLACEHOLDER} />
        </>
      )}
    </div>
  )
}
